import { Node, Port } from '@/domain/vegnett/Node';
import { SuperStedfesting } from '@/domain/vegnett/SuperStedfesting';
import { Vegsystemreferanse } from '@/domain/omrader';
import { Medium, Vegnettlenke } from '@/domain/vegnett/Vegnettlenke';
import { WKTObject } from '@/domain/WKTObject';
import type {
    Geometri as ApiGeometri,
    GeometriKvalitet as ApiGeometriKvalitet,
    SideMetadata as ApiSideMetadata,
    SosiMedium as ApiSosiMedium,
    Stedfesting as ApiStedfesting,
    Vegsystemreferanse as ApiVegsystemreferanse,
} from '@/generated/nvdb/vegobjekter/models';
import { VegnettResponse, VegnettResponseMetadata } from '../responses';
import { transformGeometri, transformKvalitet } from './geometri';
import { transformSuperstedfesting } from './stedfesting';
import { transformVegsystemreferanse } from './vegsystemreferanse';

type ApiVegnettGeometri = ApiGeometri & {
    kvalitet: ApiGeometriKvalitet;
    medium?: ApiSosiMedium;
};

type ApiSuperstedfesting = Omit<ApiStedfesting, 'sideposisjon' | 'type'> & {
    sideposisjon?: number;
};

type ApiVegnettlenkeSegment = {
    href: string;
    veglenkesekvensid: number;
    startposisjon: number;
    sluttposisjon: number;
    kortform: string;
    veglenkenummer: number;
    segmentnummer: number;
    startnode: string;
    sluttnode: string;
    referanse: string;
    type: string;
    topologinivå?: string;
    detaljnivå: string;
    typeVeg: string;
    målemetode: string;
    måledato: string;
    superstedfesting?: ApiSuperstedfesting;
    feltoversikt?: string[];
    geometri: ApiVegnettGeometri;
    lengde: number;
    fylke: number;
    kommune: number;
    vegsystemreferanse?: ApiVegsystemreferanse;
    metadata?: {
        startdato?: string;
        sluttdato?: string;
    };
};

type ApiVegnettMetadata = ApiSideMetadata;

type ApiSegmentertVegnettResponse = {
    objekter: ApiVegnettlenkeSegment[];
    metadata: ApiVegnettMetadata;
};

type ApiPort = {
    id: number;
    tilkobling: {
        portnummer: number;
        veglenkesekvensid: number;
        href: string;
    };
};

type ApiNode = {
    id: number;
    geometri: ApiGeometri;
    href: string;
    startdato: string;
    porter?: ApiPort[];
};

type VegsystemreferanseGeometriResponse = {
    geometri: WKTObject;
};

export function transformSegmentertVegnettResponse(data: null | undefined): null;
export function transformSegmentertVegnettResponse(
    data: ApiSegmentertVegnettResponse
): VegnettResponse;
export function transformSegmentertVegnettResponse(
    data: ApiSegmentertVegnettResponse | null | undefined
): VegnettResponse | null {
    return data
        ? new VegnettResponse(
              data.objekter.map(transformVegnettlenkeSegment),
              new VegnettResponseMetadata(
                  data.metadata.returnert,
                  data.metadata.antall as number,
                  data.metadata.sidestørrelse,
                  data.metadata.neste as { start: string; href: string }
              )
          )
        : null;
}

export function transformPorter(data: null | undefined): null;
export function transformPorter(data: ApiPort[]): Port[];
export function transformPorter(data: ApiPort[] | null | undefined): Port[] | null {
    return data && Array.isArray(data)
        ? data.map(p => ({
              id: p.id,
              tilkobling: {
                  portnummer: p.tilkobling.portnummer,
                  veglenkesekvensid: p.tilkobling.veglenkesekvensid,
                  href: p.tilkobling.href,
              },
          }))
        : null;
}

export function transformVegsystemreferanseGeometri(
    data: VegsystemreferanseGeometriResponse
): WKTObject {
    return data['geometri'];
}

export function transformNode(data: null | undefined): null;
export function transformNode(data: ApiNode): Node;
export function transformNode(data: ApiNode | null | undefined): Node | null {
    return data
        ? new Node(
              data.id,
              transformGeometri(data.geometri),
              data.href,
              data.startdato,
              (data.porter ? transformPorter(data.porter) : null) as Port[]
          )
        : null;
}

export function transformVegnettlenkesekvens(data: null | undefined): null;
export function transformVegnettlenkesekvens(
    data: ApiVegnettlenkeSegment | ApiVegnettlenkeSegment[]
): Vegnettlenke[];
export function transformVegnettlenkesekvens(
    data: ApiVegnettlenkeSegment | ApiVegnettlenkeSegment[] | null | undefined
): Vegnettlenke[] | null {
    if (!data) return null;
    if (Array.isArray(data)) return data.map(transformVegnettlenkeSegment);
    return data.veglenkesekvensid ? [transformVegnettlenkeSegment(data)] : null;
}

export function transformVegnettlenkeSegment(data: null | undefined): null;
export function transformVegnettlenkeSegment(data: ApiVegnettlenkeSegment): Vegnettlenke;
export function transformVegnettlenkeSegment(
    data: ApiVegnettlenkeSegment | null | undefined
): Vegnettlenke | null {
    const hasVegsysRef = Object.entries(data?.vegsystemreferanse ?? {}).length > 0;
    return data
        ? new Vegnettlenke(
              data.href,
              data.veglenkesekvensid,
              data.startposisjon,
              data.sluttposisjon,
              data.kortform,
              data.veglenkenummer,
              data.segmentnummer,
              data.startnode,
              data.sluttnode,
              data.referanse,
              data.type,
              data.topologinivå as string,
              data.detaljnivå,
              data.typeVeg,
              // data.typeVeg_sosi,
              data.målemetode,
              data.måledato,
              transformSuperstedfesting(data.superstedfesting) as SuperStedfesting,
              data.feltoversikt as string[],
              transformGeometri(data.geometri),
              transformKvalitet(data.geometri.kvalitet),
              data.lengde,
              data.fylke,
              data.kommune,
              (hasVegsysRef &&
                  transformVegsystemreferanse(
                      data.vegsystemreferanse,
                      data.kommune
                  )) as Vegsystemreferanse,
              transformMedium(data.geometri.medium),
              { startdato: data.metadata?.startdato, sluttdato: data.metadata?.sluttdato }
          )
        : null;
}

function transformMedium(medium: ApiSosiMedium | undefined): Medium | undefined {
    if (!medium || !(medium in Medium)) return undefined;
    return Medium[medium as keyof typeof Medium];
}
